import React, { useRef, useState, useEffect, useId } from "react";

/**
 * Height of the always-visible grab bar. Kept in JS because the drag math needs
 * the number; mirrored by --peek-height in index.css, which the layout
 * utilities use.
 */
const PEEK_HEIGHT = 56;
/** Movement below this is a tap, not a drag. */
const TAP_SLOP = 8;
const CLOSE_FRACTION = 0.25;
const OPEN_FRACTION = 0.15;
/** px/ms in either direction; a flick overrides the distance thresholds. */
const FLICK_VELOCITY = 0.5;
/** The iOS sheet curve. */
const SPRING = "transform 300ms cubic-bezier(0.32, 0.72, 0, 1)";

/**
 * A two-state bottom sheet: a persistent peek bar resting on the bottom nav,
 * which drags or taps up into a full entry panel.
 *
 * Geometry: the sheet is `fixed` with `bottom: var(--nav-height)`, so its bottom
 * edge *is* the nav's top edge. The rest position isn't a transform offset,
 * it's the containing block — which is why the sheet never crosses the nav at
 * any point in the animation. Expanded is translateY(0); collapsed slides down
 * by its own height minus the peek strip.
 *
 * Deliberately NOT a modal dialog. The bottom nav stays visible and interactive
 * above it, so there is no focus trap and no aria-modal — claiming modality
 * while the nav is still clickable would lie to assistive technology. This is a
 * non-modal dialog, and Tab from the last field reaching the nav is correct.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange Should be a useCallback.
 * @param {string} props.peekLabel
 * @param {string} props.title Accessible name for the dialog.
 * @param {Object} [props.peekTabs] Splits the peek bar into kind tabs instead
 *   of a single label. `{ options: [{value, label}], value, onSelect }`.
 *   The selected tab also acts as the open/close toggle.
 * @param {React.ReactNode} props.children
 */
const BottomSheet = ({ open, onOpenChange, peekLabel, title, peekTabs, children }) => {
  const sheetRef = useRef(null);
  const peekRef = useRef(null);
  const drag = useRef(null);
  const wasOpen = useRef(false);
  // State, not a ref, because the rendered `transition` depends on it. One
  // render per gesture — the per-frame offset never enters React state.
  const [dragging, setDragging] = useState(false);
  const sheetId = useId();

  const collapsedTransform = `translateY(calc(100% - ${PEEK_HEIGHT}px))`;

  const handlePointerDown = (event) => {
    const height = sheetRef.current?.offsetHeight ?? 0;
    // Optional-call because jsdom implements neither setPointerCapture nor the
    // PointerEvent constructor.
    event.target.setPointerCapture?.(event.pointerId);
    drag.current = {
      startY: event.clientY,
      lastY: event.clientY,
      lastT: event.timeStamp,
      travel: Math.max(height - PEEK_HEIGHT, 1),
      moved: false,
    };
    setDragging(true);
  };

  const handlePointerMove = (event) => {
    const state = drag.current;
    if (!state) return;

    const delta = event.clientY - state.startY;
    if (Math.abs(delta) > TAP_SLOP) state.moved = true;

    state.lastY = event.clientY;
    state.lastT = event.timeStamp;

    // Rubber-band past either end so the sheet feels attached rather than
    // clamped.
    const base = open ? 0 : state.travel;
    let offset = base + delta;
    if (offset < 0) offset *= 0.3;
    if (offset > state.travel) offset = state.travel + (offset - state.travel) * 0.3;

    // Written straight to the node. Routing this through setState would
    // re-render the sheet — and the entire entry form inside it — at pointer
    // frequency.
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${offset}px)`;
    }
  };

  const finishDrag = (event, { cancelled = false } = {}) => {
    const state = drag.current;
    if (!state) return;
    drag.current = { ...state, moved: state.moved };

    let next = open;
    if (!cancelled) {
      const delta = event.clientY - state.startY;
      const dt = event.timeStamp - state.lastT;
      // Velocity over the last move only, so a slow drag that ends in a flick
      // counts as a flick — which is how people actually dismiss sheets.
      const velocity = dt > 0 ? (event.clientY - state.lastY) / dt : 0;

      const closeAt = Math.min(120, state.travel * CLOSE_FRACTION);
      const openAt = Math.min(80, state.travel * OPEN_FRACTION);

      if (velocity > FLICK_VELOCITY) next = false;
      else if (velocity < -FLICK_VELOCITY) next = true;
      else if (open) next = delta <= closeAt;
      else next = delta < -openAt;
    }

    // Both of these land in one React 18 batch, so the final rest transform and
    // the restored transition are applied in a single style commit — the
    // imperative transform above is overwritten with no intermediate frame.
    // Clearing style.transform by hand first is what would cause a flash.
    setDragging(false);
    onOpenChange(next);
  };

  const handlePointerUp = (event) => finishDrag(event);
  const handlePointerCancel = (event) => finishDrag(event, { cancelled: true });

  // A drag that ends over a button also fires click. Without this the sheet
  // would act twice: once from the gesture, once from the click. Keyboard
  // Enter/Space fires click with no preceding pointer sequence, so `moved` is
  // false and the button behaves normally.
  const consumedByDrag = () => {
    if (drag.current?.moved) {
      drag.current.moved = false;
      return true;
    }
    return false;
  };

  const handleClick = () => {
    if (consumedByDrag()) return;
    onOpenChange(!open);
  };

  const handleTabClick = (value, isSelected) => {
    if (consumedByDrag()) return;
    // The selected tab *is* the handle, so it toggles. The other one switches
    // kind, and the caller decides whether that also opens the sheet.
    if (isSelected) onOpenChange(!open);
    else peekTabs.onSelect(value);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Restored in cleanup as well as on close, so unmounting mid-open can't
    // leave the page permanently unscrollable. Deliberately not the
    // `position: fixed` body trick: that resets scroll position, and any
    // transform on an ancestor would make it the containing block for the
    // fixed nav and sheet, breaking both.
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      sheetRef.current?.focus();
    } else if (wasOpen.current) {
      // Only on a close that followed an open — not on mount, which would steal
      // focus on first paint.
      peekRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  return (
    <>
      {open && (
        <div
          onClick={() => onOpenChange(false)}
          aria-hidden="true"
          // Stops at the nav's top edge rather than inset-0. A full-bleed scrim
          // underneath the nav would tint it, because the nav is bg-white/80.
          className="fixed left-0 right-0 top-0 bottom-[var(--nav-height)] z-30 bg-black/30 touch-none"
        />
      )}

      <div
        ref={sheetRef}
        id={sheetId}
        role="dialog"
        aria-label={title}
        tabIndex={-1}
        // bg-gray-50, a shade off the nav's bg-white/95: the two surfaces meet
        // along the sheet's bottom edge, and matching whites left only the
        // nav's top border to separate them. It also turns the sheet into a
        // canvas that the form's white controls read as cards on.
        className="fixed left-0 right-0 bottom-[var(--nav-height)] z-40 h-[85vh] bg-gray-50 rounded-t-3xl shadow-2xl border-t border-gray-200 flex flex-col will-change-transform focus:outline-none"
        style={{
          // Inline because Tailwind can express neither: the collapsed offset
          // depends on the element's own height, and the transition has to be
          // switched off mid-gesture.
          transform: open ? "translateY(0)" : collapsedTransform,
          transition: dragging ? "none" : SPRING,
        }}
      >
        {/* The peek bar is the collapsed state, the drag handle, and the
            toggle. The pointer handlers live ONLY on this wrapper, which is
            what structurally guarantees a numpad tap can never start a sheet
            drag: the drag code never sees pointer events from the body at all.
            Do not hoist them to the container "for convenience".

            It's a plain div rather than a button because it can contain the
            kind tabs, and a button can't nest buttons. */}
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          style={{ height: PEEK_HEIGHT }}
          // touch-none, or the browser claims the vertical pan and fires
          // pointercancel mid-gesture — drag works on desktop and dies on iOS.
          className="w-full shrink-0 flex flex-col items-center justify-center gap-1.5 touch-none select-none cursor-grab active:cursor-grabbing"
        >
          <span aria-hidden="true" className="block w-10 h-1 rounded-full bg-gray-300" />

          {peekTabs ? (
            <div role="group" aria-label="Entry type" className="w-full flex gap-1 px-3">
              {peekTabs.options.map((option) => {
                const isSelected = option.value === peekTabs.value;
                return (
                  <button
                    key={option.value}
                    ref={isSelected ? peekRef : null}
                    type="button"
                    aria-pressed={isSelected}
                    // The selected tab doubles as the disclosure control, so it
                    // carries aria-expanded; the other is only a kind switch.
                    aria-expanded={isSelected ? open : undefined}
                    aria-controls={isSelected ? sheetId : undefined}
                    onClick={() => handleTabClick(option.value, isSelected)}
                    className={`flex-1 py-1 rounded-lg text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 ${
                      isSelected
                        ? "bg-white shadow-sm text-gray-900 font-semibold"
                        : "text-gray-400 font-medium hover:text-gray-600"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <button
              ref={peekRef}
              type="button"
              aria-expanded={open}
              aria-controls={sheetId}
              onClick={handleClick}
              className="w-full text-sm font-medium text-gray-600 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            >
              {peekLabel}
            </button>
          )}
        </div>

        {/* touch-pan-y so native scrolling works here and is never contended by
            the drag handler, which lives only on the bar above. */}
        <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y">
          {children}
        </div>
      </div>
    </>
  );
};

export default BottomSheet;
